import { createSelector } from '@reduxjs/toolkit';
import type {
  AiPredictionRead,
  AiPreferences,
  AiWorkspaceDefaults,
  DerivedAiStatus,
  ResolvedWorkspacePreferences,
  TimelyPresentableAiPrediction,
  TimesheetAssistant,
} from '@timelyapp/integration-cloud-api-spec';
import { TimesheetAssistantSchema } from '@timelyapp/integration-cloud-api-spec';
import type { DemoMemoryBlock } from '@timelyapp/integration-cloud-sdk/memory-demo-data/types';
import {
  consolidatePredictionsAsDateMap,
  removeOverlapFromGenericInterval,
} from '@timelyapp/integration-cloud-sdk/utils';
import { consolidatePredictions } from '@timelyapp/integration-cloud-sdk/utils/ai_predictions/consolidate-predictions';
import { deriveAiStatus } from '@timelyapp/integration-cloud-sdk/utils/ai_predictions/derive-ai-status';
import { coerceAiPreferences } from '@timelyapp/integration-cloud-sdk/utils/ai_predictions/util.coerceAiPreferences';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { DateTime } from 'luxon';
import { getTagEntities } from 'redux/modules/tags.selectors';
import { getCurrentAccountId, getHasTicAiFeature } from 'redux/selectors/accounts';
import { getDates, getDerivedDatesInfo } from 'redux/selectors/calendar.filter';
import { getInjectSyntheticData } from 'redux/selectors/development';
import { getHourXhrUIState, getUnprocessedHours } from 'redux/selectors/hours';
import { getHasHourDraft } from 'redux/selectors/hours.draft';
import { getHourDraftProjectId } from 'redux/selectors/hours.draft.base';
import { getAdjustableTimelineTimestamps } from 'redux/selectors/hours.timestamps.base.selector';
import { getEnrichedProjectsForCurrentUserById } from 'redux/selectors/projects';
import { getUserId } from 'redux/selectors/user';
import { prefixHexColor } from 'utils/colors';
import { fromISO } from 'utils/luxon-helpers';
import { getProjectColor } from 'utils/project';
import { lazySelector } from 'utils/reselect';
import {
  adjustBlocksToSummaryBlocks,
  generateSyntheticPredictions,
} from 'utils/tic/tic-generate-synthetic-predictions';
import { ticPredictionMatchesHourId } from 'utils/tic/tic-prediction-matches-hour-id';
import { isTicPredictionValidAsHour } from 'utils/tic/tic-prediction-valid-as-hour';
import { getTicApiSlice } from '~/redux/modules/tic/api-slice/tic.api.selector';
import { ticApi } from '~/redux/modules/tic/api-slice/tic.apiSlice';
import { getTicViewDemoMode } from '~/redux/modules/tic/tic.demo.selectors';
import { getDemoMemoriesData } from '~/redux/modules/tic/tic.memories.synthetic.selectors';

/**
 * Feed this to the `useGetTimeIntervalPredictionsQuery` hook to ensure hook & selector are in sync.
 */
export const getPredictionQuery = createSelector(
  [getDates, getCurrentAccountId],
  (calendarDates, timelyAccountId) => {
    const from = calendarDates[0];
    const to = calendarDates[calendarDates.length - 1];

    const startDate = fromISO(from).startOf('day').toISO();
    const endDate = fromISO(to).endOf('day').toISO();

    return {
      startDate,
      endDate,
      // todo: remove ^^, go with below instead, but for now..
      startedAt: startDate,
      endedAt: endDate,
      timelyAccountId,
    };
  }
);

/** Feed into selectors to get at getAiPredictionStatusForDateRange data */
export const getAiPredictionStatusForDateQuery = createSelector(
  [getUserId, getCurrentAccountId, getDerivedDatesInfo],
  (timelyUserId, timelyAccountId, dateMeta) => {
    return { fromDate: dateMeta.since, toDate: dateMeta.upto, timelyAccountId, timelyUserId };
  }
);

/**
 * AI Status RTKQ Base Selector
 */
const getAiPredictionStatusForDateRangeRTKQueryBase = createSelector(
  [getTicApiSlice, getAiPredictionStatusForDateQuery],
  (localState, query) => {
    return ticApi.endpoints.getAiPredictionStatusForDateRange.select(query)({
      [ticApi.reducerPath]: localState,
    });
  }
);

/**
 * AI Status Data Response
 * @todo Re-use `transformLogsToStatuses` from `queryGetAiPreferences.utils.ts` (in TIC, move it),
 *       so that we can do client-side injections of new state.
 */
export const getAiPredictionStatusForDateRangeRTKQueryData = createSelector(
  [getAiPredictionStatusForDateRangeRTKQueryBase],
  res => res.data
);

/** Is `training_requested` or 'training_running' current state */
export const getIsAiCurrentlyTraining = createSelector(
  [getAiPredictionStatusForDateRangeRTKQueryData],
  (data): boolean => {
    return !!data?.status.some(s => ['training_requested', 'training_running'].includes(s.event));
  }
);

/** Is `inference_requested` current state */
export const getIsAiCurrentlyInferring = createSelector(
  [getAiPredictionStatusForDateRangeRTKQueryData],
  (data): boolean => {
    return !!data?.status.some(s => ['inference_requested'].includes(s.event));
  }
);

/** Is AI currently working? (training or inferring) */
export const getIsAiCurrentlyWorking = createSelector(
  [getIsAiCurrentlyTraining, getIsAiCurrentlyInferring],
  (isTraining, isInferring): boolean => isTraining || isInferring
);

/**
 * AI Preferences RTKQ Base Selector
 */
export const getTicAiPreferencesRTKQueryBase = createSelector([getTicApiSlice], localState => {
  return ticApi.endpoints.getAiPreferences.select(undefined)({ [ticApi.reducerPath]: localState });
});

const getTicAiPreferencesRTKQueryData = createSelector(
  [getTicAiPreferencesRTKQueryBase],
  ({ data }): AiPreferences | undefined => data as AiPreferences // Intent: Avoid re-calc when non-data changes on RTKQ subscription.
);

/**
 * Reads AI preferences from RTKQ and adjusts some values to ensure they're in a consistent state.
 * e.g, can't show predictions if AI is disabled.
 *
 * @note Preferences only load for TIC+internal employees, so entirely disabled by default.
 * @ref `useTicPredictionIntervalsLoader.ts`
 */
export const getTicAiPreferences = createSelector([getTicAiPreferencesRTKQueryData], data => {
  return coerceAiPreferences(data).preferences;
});

/**
 * Workspace Preferences RTKQ Base Selector
 * Returns the resolved workspace-level timesheet assistant preferences, keyed by account ID.
 */
const getTicAiWorkspacePreferencesRTKQueryBase = createSelector(
  [getTicApiSlice, getCurrentAccountId],
  (localState, timelyAccountId) => {
    return ticApi.endpoints.getAiWorkspacePreferences.select({ timelyAccountId })({
      [ticApi.reducerPath]: localState,
    });
  }
);

/**
 * Returns the resolved workspace preferences data (timesheet_assistant + locked_fields).
 */
export const getTicAiWorkspacePreferencesData = createSelector(
  [getTicAiWorkspacePreferencesRTKQueryBase],
  ({ data }): ResolvedWorkspacePreferences | undefined => data as ResolvedWorkspacePreferences
);

/**
 * Returns the resolved timesheet_assistant settings for the current workspace.
 * Falls back to system defaults if no workspace preferences exist.
 */
export const getResolvedTimesheetAssistant = createSelector(
  [getTicAiWorkspacePreferencesData],
  (workspacePrefs): TimesheetAssistant => {
    return workspacePrefs?.timesheet_assistant ?? TimesheetAssistantSchema.parse({});
  }
);

/**
 * Returns the list of locked (admin-enforced) fields for the current workspace.
 */
export const getLockedTimesheetAssistantFields = createSelector(
  [getTicAiWorkspacePreferencesData],
  (workspacePrefs): string[] => {
    return workspacePrefs?.locked_fields ?? [];
  }
);

/**
 * Workspace Defaults RTKQ Base Selector (admin-enforced settings).
 */
const getTicAiWorkspaceDefaultsRTKQueryBase = createSelector(
  [getTicApiSlice, getCurrentAccountId],
  (localState, timelyAccountId) => {
    return ticApi.endpoints.getAiWorkspaceDefaults.select({ timelyAccountId })({
      [ticApi.reducerPath]: localState,
    });
  }
);

/**
 * Returns the workspace defaults data (admin-enforced timesheet_assistant + locked_fields).
 */
export const getTicAiWorkspaceDefaultsData = createSelector(
  [getTicAiWorkspaceDefaultsRTKQueryBase],
  ({ data }) => data as AiWorkspaceDefaults | undefined
);

/**
 * Returns whether workspace defaults have been loaded.
 */
export const getTicAiWorkspaceDefaultsIsLoading = createSelector(
  [getTicAiWorkspaceDefaultsRTKQueryBase],
  ({ isLoading }) => isLoading
);

/** Returns coerced `show_timeline_predictions` */
export const getTicAiPreferenceShowTimelinePredictions = createSelector(
  [getTicAiPreferences],
  (preferences): boolean => preferences.show_timeline_predictions
);

const getMemoryDateRange = lazySelector(
  import('~/redux/modules/tic/tic.memories.selectors').then(m => m.getMemoryDateRange)
);
const getMemoryConfig = lazySelector(
  import('~/redux/modules/tic/tic.memories.config.selectors').then(m => m.getMemoryConfig)
);
const getTicMemorySummaryBlocks = lazySelector(
  import('~/redux/modules/tic/tic.memories.selectors').then(m => m.getTicMemorySummaryBlocks)
);
/**
 * Returns TIC-specific synthetic/demo predictions when either:
 * * `injectSyntheticData` is enabled (DevTools)
 * * `viewDemoMode` is set to an ideal customer profile (TIC)
 */
export const getTicSyntheticPredictions = createSelector(
  [
    getTicViewDemoMode,
    getInjectSyntheticData,
    getDemoMemoriesData,
    getEnrichedProjectsForCurrentUserById,
    getTicAiPreferences,
    getTagEntities,
    getMemoryConfig,
    getTicMemorySummaryBlocks,
  ],
  (
    ticViewDemoMode,
    enableSynthetics,
    demoMemoriesData,
    enrichedProjectForUserIdMap,
    aiPreferences,
    tagEntities,
    memoryConfig,
    ticSummaryBlocks
  ): AiPredictionRead[] => {
    if (!ticViewDemoMode.enabled && !enableSynthetics) return EMPTY_ARRAY;

    let memoryBlocks: Array<Omit<DemoMemoryBlock, 'aiNotes'>> = demoMemoriesData.blocks;
    if ((memoryConfig?.minutesPerBlock ?? 0) > 0 && ticSummaryBlocks?.blocks.length) {
      memoryBlocks = adjustBlocksToSummaryBlocks(demoMemoriesData.blocks, ticSummaryBlocks.blocks);
    }
    return generateSyntheticPredictions({
      fromDateTime: DateTime.now().startOf('day'),
      aiPreferences,
      memoryBlocks,
      enrichedProjectForUserIdMap,
      tagEntities,
    });
  }
);

const getLastRelevantMemoryEndingForAiStatus = lazySelector(
  import('~/redux/modules/tic/tic.memories.selectors').then(
    m => m.getLastRelevantMemoryEndingForAiStatus
  )
);

/**
 * Derived AI Status, great for status & UI indicators.
 */
export const getAiDerivedStatus = createSelector(
  [
    getAiPredictionStatusForDateRangeRTKQueryData,
    getDerivedDatesInfo,
    getLastRelevantMemoryEndingForAiStatus,
    getHasTicAiFeature,
    getTicAiPreferences,
  ],
  (
    rawData,
    { since, upto },
    last_memory_updated_at,
    ai_supported,
    { enabled }
  ): DerivedAiStatus => {
    const data = rawData || { status: [], dates_updated_at: {} };
    return deriveAiStatus({ from_date: since, to_date: upto }, data, {
      ai_supported,
      ai_enabled: enabled,
      last_memory_updated_at,
    });
  }
);

/**
 * This might scale poorly/when there's lots of chatter on the local slice.
 * Maybe split up namespace of ticApi slices further if so?
 * @ref `reducer.ts` and `openapi-tic-rtkquery-config.cjs`
 */
export const getTicPredictionsRTKQueryBase = createSelector(
  [getTicApiSlice, getPredictionQuery],
  (localState, query) => {
    return ticApi.endpoints.getAiPredictionsInDateRange.select(query)({
      [ticApi.reducerPath]: localState,
    });
  }
);
/**
 * Returns the AI predictions from the RTKQ slice.
 * _and_ ensures result-set include/exclude certain predicted properties based on user preference.
 */
export const getPredictedIntervals = createSelector(
  [
    getTicPredictionsRTKQueryBase,
    getTicSyntheticPredictions,
    getTicAiPreferences,
    getTicViewDemoMode,
  ],
  (
    { data },
    syntheticPredictions,
    { suggest_note, train_tags },
    viewDemoMode
  ): AiPredictionRead[] => {
    if (viewDemoMode.enabled) {
      return syntheticPredictions;
    }
    if (!Array.isArray(data?.ai_predictions)) return EMPTY_ARRAY;

    return data.ai_predictions.map(prediction => {
      const { inferred } = prediction;
      return {
        ...prediction,
        inferred: {
          ...inferred,
          note: suggest_note && !!inferred.note ? inferred.note : undefined,
          timely_label_ids: train_tags ? inferred.timely_label_ids : EMPTY_ARRAY,
        },
      };
    }) as AiPredictionRead[];
  }
);

/**
 * AI predictions, minus any rejections (server-side atm) and logged time (client-side)
 */
export const getPresentableAiPredictionsExceptLoggedTime = createSelector(
  [
    getTicAiPreferences,
    getPredictedIntervals,
    getAdjustableTimelineTimestamps,
    getEnrichedProjectsForCurrentUserById,
    getHourDraftProjectId,
    getTagEntities,
    getUnprocessedHours,
    getUserId,
  ],
  (
    ai_preferences,
    ai_predictions,
    { adjustableRanges: preoccupiedIntervals },
    enrichedProjectForUserIdMap,
    hourDraftProjectId,
    tagEntities,
    hours,
    userId
  ): TimelyPresentableAiPrediction[] => {
    // Early bailout if no relevant data yet.
    if (!ai_preferences.enabled || !ai_predictions.length || !enrichedProjectForUserIdMap) {
      return EMPTY_ARRAY;
    }
    // Remove overlaps from ai_predictions
    const adjustedIntervals = ai_predictions.flatMap(interval =>
      removeOverlapFromGenericInterval(interval, preoccupiedIntervals)
    );

    // Map over the non-overlapping ai_predictions to create the TimelyPresentableAiPrediction array
    const res = adjustedIntervals.map(prediction => {
      const projectRef = enrichedProjectForUserIdMap[prediction.inferred.timely_project_id];
      const project = projectRef?.fullProject || projectRef?.base || null;

      // Only keep label ids that still resolve to a tag in the store. A deleted
      // or not-visible tag id is absent from `tagEntities`, and destructuring
      // the resulting `undefined` below crashes the whole render (TT-5614).
      const resolvableLabelIds = (prediction?.inferred.timely_label_ids || EMPTY_ARRAY).filter(
        labelId => tagEntities[labelId] != null
      );

      const labels = resolvableLabelIds.map(labelId => {
        const { id, name, emoji } = tagEntities[labelId];
        return { id, name, emoji };
      });

      // Client-side verifies AI's valid_as_hour assumption
      const valid_as_hour = isTicPredictionValidAsHour({ prediction, project, userId });
      // Only match prediction to existing hour.id if project is usable/not archived
      const existing_hour_id = !valid_as_hour
        ? null
        : ticPredictionMatchesHourId(prediction, hours, ai_preferences);

      const started_at = prediction.started_at as DateTime<true>;
      const ended_at = prediction.ended_at as DateTime<true>;

      return {
        id: prediction.id!,
        date: started_at.toISODate(),
        started_at,
        ended_at,
        // Calculate total seconds from interval
        total_seconds: ended_at.diff(started_at, 'seconds').seconds,
        confidence: prediction.inferred.confidence || 0,
        color: prefixHexColor(getProjectColor(project)),
        project,
        note: prediction.inferred.note,
        // We can assume this prediction can be amended to this existing `hour.id`
        existing_hour_id,
        valid_as_hour,
        project_id: project?.id,
        memory_ids: prediction.inferred.memory_ids || EMPTY_ARRAY,
        label_ids: resolvableLabelIds.length ? resolvableLabelIds : EMPTY_ARRAY,
        labels: labels,
        collapsed:
          !!hourDraftProjectId && hourDraftProjectId !== prediction?.inferred.timely_project_id,
        timestamps: [
          {
            started_at,
            ended_at,
          },
        ],

        // The hourId can be determined here if needed, using additional logic
      } satisfies TimelyPresentableAiPrediction;
    });

    return res;
  }
);

/**
 * Predictions formatted for `TicMemoryTimeline` views.
 */
export const getPredictedIntervalsForTicTimeline = createSelector(
  [getTicAiPreferences, getPresentableAiPredictionsExceptLoggedTime, getMemoryDateRange],
  (preferences, ai_predictions, dateRange) => {
    if (!dateRange) return EMPTY_ARRAY;
    const { startDate, endDate } = dateRange;
    // Bail out early if not enabled
    if (!preferences.show_timeline_predictions) return EMPTY_ARRAY;

    return ai_predictions.filter(interval => {
      return interval.started_at >= fromISO(startDate) && interval.ended_at <= fromISO(endDate);
    });
  }
);

/**
 * Predictions formatted for `Calendar` views.
 * Partial predictions are merged into a single interval for calendar display.
 */
export const getPredictedIntervalsForTimesheetDates = createSelector(
  [getTicAiPreferences, getPresentableAiPredictionsExceptLoggedTime],
  (preferences, ai_predictions): Record<string, TimelyPresentableAiPrediction[]> => {
    // Bail out early if not enabled
    if (!preferences.show_timesheet_predictions) {
      return EMPTY_OBJECT;
    }

    return consolidatePredictionsAsDateMap(ai_predictions);
  }
);

/**
 * Consolidated predictions
 */
export const getConsolidatedPredictedIntervals = createSelector(
  [getPresentableAiPredictionsExceptLoggedTime],
  (uniquePredictions): TimelyPresentableAiPrediction[] => {
    return consolidatePredictions(uniquePredictions);
  }
);

/**
 * Consolidated predictions, with contextual meta.
 */
export const getConsolidatedPredictedIntervalsWithMeta = createSelector(
  [getConsolidatedPredictedIntervals, getHourXhrUIState, getHasHourDraft],
  (predictions, hourXhr, hasOpenHourDraft): GetConsolidatedPredictedIntervalsWithMetaReturn => {
    const { totalSeconds, validAsHourSeconds } = predictions.reduce(
      (acc, { total_seconds, valid_as_hour }) => ({
        totalSeconds: acc.totalSeconds + total_seconds,
        validAsHourSeconds: acc.validAsHourSeconds + (valid_as_hour ? total_seconds : 0),
      }),
      { totalSeconds: 0, validAsHourSeconds: 0 }
    );
    const xhrState = hourXhr.isSaving ? 'saving' : hourXhr.isLoading ? 'loading' : 'idle';

    const hint =
      xhrState === 'loading'
        ? undefined
        : hasOpenHourDraft
          ? 'You must save changes first'
          : totalSeconds === 0
            ? 'No suggestions to save'
            : 'Save all suggestions';

    const isMagicActionUnavailable =
      validAsHourSeconds === 0 || ['loading', 'saving'].includes(xhrState) || hasOpenHourDraft;

    return {
      predictions,
      meta: {
        hint,
        xhrState,
        totalSeconds,
        validAsHourSeconds,
        hasOpenHourDraft,
        isMagicActionUnavailable,
      },
    };
  }
);

type GetConsolidatedPredictedIntervalsWithMetaReturn = {
  predictions: TimelyPresentableAiPrediction[];
  meta: {
    xhrState: 'idle' | 'loading' | 'saving';
    hint: string | undefined;
    /** Total seconds (remaining), even if prediction isn't directly loggable  */
    totalSeconds: number;
    /** Seconds (remaining) that are valid_as_hour/can be auto-created */
    validAsHourSeconds: number;
    hasOpenHourDraft: boolean;
    /** Hints at Magic action to be disabled (pair with local state checks as needed) */
    isMagicActionUnavailable: boolean;
  };
};
